import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Stack,
  CardActions,
  Divider,
} from '@mui/material';
import { 
  CheckCircle, 
  Cancel, 
  Info, 
  Refresh,
  HourglassEmpty,
  MoreVert,
  History as HistoryIcon,
  CheckCircleOutline,
  CancelOutlined,
  Router as RouterIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import httpClient from '../services/httpClient';

/**
 * Dashboard for managing pending gateway registration requests
 * Shows real-time updates and allows admin to approve/reject requests
 * Also displays approval history
 */
function GatewayApprovalDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0 = Pending, 1 = History

  useEffect(() => {
    fetchData();
    
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    await Promise.all([
      fetchPendingRequests(),
      fetchApprovalHistory(),
    ]);
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await httpClient.get('/v1/admin/gateway-requests/pending');
      setPendingRequests(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch pending requests:', err);
      setError('Failed to load pending requests: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalHistory = async () => {
    try {
      const response = await httpClient.get('/v1/admin/gateway-requests/history');
      setApprovalHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch approval history:', err);
    }
  };

  const handleApprove = async (requestId) => {
    setLoading(true);
    try {
      const response = await httpClient.post(`/v1/admin/gateway-requests/${requestId}/approve`);
      setSuccessMessage(`Gateway "${response.data.deviceName}" approved successfully! VPN IP: ${response.data.assignedIp}`);
      await fetchData();
      setShowDetailModal(false);
    } catch (err) {
      setError('Failed to approve registration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    
    setLoading(true);
    try {
      await httpClient.post(`/v1/admin/gateway-requests/${selectedRequest.registrationId}/reject`, {
        reason: rejectionReason
      });
      setSuccessMessage(`Gateway "${selectedRequest.deviceName}" rejected`);
      await fetchData();
      setShowRejectModal(false);
      setShowDetailModal(false);
      setRejectionReason('');
    } catch (err) {
      setError('Failed to reject registration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setShowRejectModal(true);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Helper function to render approval request cards (mobile)
  const renderPendingRequestCard = (request) => (
    <Card key={request.registrationId} sx={{ mb: 2 }}>
      <CardContent>
        {/* Gateway Name & Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RouterIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {request.deviceName}
            </Typography>
            <Chip label={request.deviceVersion} size="small" />
          </Box>
        </Box>

        {/* Gateway Details */}
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'start' }}>
            <LocationIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="textSecondary">Location</Typography>
              <Typography variant="body2">{request.deviceLocation || 'N/A'}</Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Source IP</Typography>
            <Typography variant="body2" fontFamily="monospace">
              {request.sourceIp}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Requested At</Typography>
            <Typography variant="body2">{formatTimestamp(request.requestedAt)}</Typography>
          </Box>
        </Stack>
      </CardContent>
      <Divider />
      <CardActions sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Info />}
          onClick={() => openDetailModal(request)}
        >
          View Details
        </Button>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={() => handleApprove(request.registrationId)}
            disabled={loading}
          >
            Approve
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            onClick={() => openRejectModal(request)}
            disabled={loading}
          >
            Reject
          </Button>
        </Box>
      </CardActions>
    </Card>
  );

  // Helper function to render history cards (mobile)
  const renderHistoryCard = (request) => (
    <Card key={request.registrationId} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RouterIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight="bold">
              {request.deviceName}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {request.deviceLocation || 'N/A'}
            </Typography>
          </Box>
          <Chip 
            label={request.status}
            color={request.status === 'APPROVED' ? 'success' : 'error'}
            size="small"
            icon={request.status === 'APPROVED' ? <CheckCircleOutline /> : <CancelOutlined />}
          />
        </Box>

        <Stack spacing={1}>
          <Box>
            <Typography variant="caption" color="textSecondary">Requested</Typography>
            <Typography variant="body2">{formatTimestamp(request.requestedAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Processed</Typography>
            <Typography variant="body2">{formatTimestamp(request.processedAt)}</Typography>
          </Box>
          {request.processedBy && (
            <Box>
              <Typography variant="caption" color="textSecondary">Processed By</Typography>
              <Typography variant="body2">{request.processedBy}</Typography>
            </Box>
          )}
          {request.rejectionReason && (
            <Box>
              <Typography variant="caption" color="error">Rejection Reason</Typography>
              <Typography variant="body2" color="error">{request.rejectionReason}</Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading && pendingRequests.length === 0 && approvalHistory.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading pending requests...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HourglassEmpty sx={{ mr: 1, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Gateway Registrations
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<Refresh />}
          onClick={fetchData} 
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(event, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Pending Requests" icon={<HourglassEmpty />} />
        <Tab label="Approval History" icon={<HistoryIcon />} />
      </Tabs>

      {tabValue === 0 ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HourglassEmpty sx={{ mr: 1, fontSize: 32 }} />
              <Typography variant="h4" component="h1">
                Pending Gateway Registrations
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />}
              onClick={fetchPendingRequests} 
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {pendingRequests.length === 0 ? (
            <Paper elevation={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 8 }}>
                  <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    No Pending Requests
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    All gateway registration requests have been processed.
                  </Typography>
                </CardContent>
              </Card>
            </Paper>
          ) : (
            isMobile ? (
              // Mobile Card View
              <Box>
                {pendingRequests.map((request) => renderPendingRequestCard(request))}
              </Box>
            ) : (
              // Desktop Table View
              <TableContainer component={Paper} elevation={3}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Device Name</strong></TableCell>
                      <TableCell><strong>Location</strong></TableCell>
                      <TableCell><strong>Source IP</strong></TableCell>
                      <TableCell><strong>Requested At</strong></TableCell>
                      <TableCell><strong>Version</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                      {pendingRequests.map((request) => (
                        <TableRow key={request.registrationId} hover>
                          <TableCell>
                            <Typography variant="body1" fontWeight="medium">
                              {request.deviceName}
                            </Typography>
                          </TableCell>
                          <TableCell>{request.deviceLocation || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                              {request.sourceIp}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatTimestamp(request.requestedAt)}</TableCell>
                          <TableCell>
                            <Chip label={request.deviceVersion} size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Button 
                                variant="outlined" 
                                size="small"
                                startIcon={<Info />}
                                onClick={() => openDetailModal(request)}
                              >
                                Details
                              </Button>
                              <Button 
                                variant="contained" 
                                color="success"
                                size="small"
                                startIcon={<CheckCircle />}
                                onClick={() => handleApprove(request.registrationId)}
                                disabled={loading}
                              >
                                Approve
                              </Button>
                              <Button 
                                variant="contained" 
                                color="error"
                                size="small"
                                startIcon={<Cancel />}
                                onClick={() => openRejectModal(request)}
                                disabled={loading}
                              >
                                Reject
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}

          {/* Detail Modal */}
          <Dialog 
            open={showDetailModal} 
            onClose={() => setShowDetailModal(false)} 
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Gateway Registration Details</DialogTitle>
            <DialogContent dividers>
              {selectedRequest && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Device Information
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Device Name</strong></TableCell>
                          <TableCell>{selectedRequest.deviceName}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Location</strong></TableCell>
                          <TableCell>{selectedRequest.deviceLocation || 'Not specified'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Version</strong></TableCell>
                          <TableCell>{selectedRequest.deviceVersion}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>MAC Address</strong></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {selectedRequest.macAddress}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="h6" gutterBottom>
                    Network Information
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Source IP</strong></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {selectedRequest.sourceIp}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>WireGuard Public Key</strong></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                              {selectedRequest.wireguardPublicKey?.substring(0, 40)}...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="h6" gutterBottom>
                    Request Details
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Registration ID</strong></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                              {selectedRequest.registrationId}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Requested At</strong></TableCell>
                          <TableCell>{formatTimestamp(selectedRequest.requestedAt)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell>
                            <Chip label={selectedRequest.status} color="warning" size="small" />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button 
                variant="contained" 
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => handleApprove(selectedRequest.registrationId)}
                disabled={loading}
              >
                Approve Gateway
              </Button>
              <Button 
                variant="contained"
                color="error"
                startIcon={<Cancel />}
                onClick={() => {
                  setShowDetailModal(false);
                  openRejectModal(selectedRequest);
                }}
                disabled={loading}
              >
                Reject Gateway
              </Button>
              <Button variant="outlined" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reject Modal */}
          <Dialog 
            open={showRejectModal} 
            onClose={() => setShowRejectModal(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Reject Gateway Registration</DialogTitle>
            <DialogContent dividers>
              {selectedRequest && (
                <>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    You are about to reject the registration for <strong>{selectedRequest.deviceName}</strong>.
                  </Alert>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Rejection Reason"
                    placeholder="Enter reason for rejection..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    required
                    variant="outlined"
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button 
                variant="contained"
                color="error"
                onClick={handleReject}
                disabled={loading || !rejectionReason.trim()}
              >
                {loading ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
              <Button variant="outlined" onClick={() => setShowRejectModal(false)}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Paper elevation={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HistoryIcon sx={{ mr: 1, fontSize: 32 }} />
              <Typography variant="h4" component="h1">
                Approval History
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />}
              onClick={fetchApprovalHistory} 
              disabled={loading}
            >
              Refresh History
            </Button>
          </Box>

          {approvalHistory.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <CheckCircleOutline sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  No Approval History
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  No gateway registrations have been approved or rejected yet.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            isMobile ? (
              // Mobile Card View
              <Box>
                {approvalHistory.map((request) => renderHistoryCard(request))}
              </Box>
            ) : (
              // Desktop Table View
              <TableContainer component={Paper} elevation={3}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Device Name</strong></TableCell>
                      <TableCell><strong>Location</strong></TableCell>
                      <TableCell><strong>Source IP</strong></TableCell>
                      <TableCell><strong>Requested At</strong></TableCell>
                      <TableCell><strong>Version</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Approved/Rejected By</strong></TableCell>
                      <TableCell><strong>Approved/Rejected At</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvalHistory.map((request) => (
                      <TableRow key={request.registrationId} hover>
                        <TableCell>{request.deviceName}</TableCell>
                        <TableCell>{request.deviceLocation || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            {request.sourceIp}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatTimestamp(request.requestedAt)}</TableCell>
                        <TableCell>
                          <Chip label={request.deviceVersion} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={request.status} color={request.status === 'APPROVED' ? 'success' : 'error'} size="small" />
                        </TableCell>
                        <TableCell>{request.approvedBy || 'N/A'}</TableCell>
                        <TableCell>{formatTimestamp(request.approvedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </Paper>
      )}
    </Container>
  );
}

export default GatewayApprovalDashboard;

